import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowUpRight, Building2, HardHat, Link2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { practiceApi } from "../../lib/practiceApi";

const api = (practiceId, path = "", options) => practiceApi(`/practices/${practiceId}/site${path}`, options);

export const SiteLinkCard = ({ practice, onChanged, onNavigate }) => {
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("");
  const [draft, setDraft] = useState({});
  const [siteId, setSiteId] = useState("");
  const [removing, setRemoving] = useState(false);
  const [step, setStep] = useState("choice");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const practiceId = practice.id;
  const load = useCallback(() => api(practiceId, "-link").then(setInfo).catch(err => setError(err.message)), [practiceId]);
  useEffect(() => { load(); }, [load, practice.version]);

  async function run(action) {
    setBusy(true); setError("");
    try { await action(); await load(); onChanged(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  function openCreate() {
    setDraft({ name: info?.prefill.name || "", address: info?.prefill.address || "", municipality: info?.prefill.municipality || "", is_coordination: false });
    setMode("create");
  }
  const send = data => api(practiceId, "", { method: "POST", body: JSON.stringify({ data }) });
  const linked = info?.linked;
  const suggestion = info?.suggestion?.status === "pending" ? info.suggestion : null;
  const impact = info?.impact || {};

  return <section className="practice-section site-flag-card" data-testid="practice-site-flag">
    <div className="practice-section-heading">
      <h2><HardHat size={17} />Cantiere</h2>
      {info?.is_site
        ? <Button variant="outline" className="ghost" disabled={busy} data-testid="practice-site-unflag" onClick={() => { setRemoving(true); setStep("choice"); setConfirm(""); setError(""); }}><X size={15} />Non è un cantiere</Button>
        : <Button className="primary" disabled={busy} data-testid="practice-site-flag-on" onClick={() => setMode(m => m ? "" : "choose")}><Plus size={15} />Segna come cantiere</Button>}
    </div>

    {suggestion && !info.is_site && <div className="site-flag-suggestion" data-testid="practice-site-suggestion">
      <Sparkles size={15} />
      <div>
        <strong>L'AI ritiene che questa pratica comporti un cantiere</strong>
        <small>{suggestion.reason || "Rilevati lavori edili nei documenti caricati."}{suggestion.filename ? ` · ${suggestion.filename}` : ""}</small>
      </div>
      <div className="site-flag-suggestion-actions">
        <Button className="primary" disabled={busy} data-testid="practice-site-suggestion-accept" onClick={openCreate}>Crea il cantiere</Button>
        <Button variant="ghost" disabled={busy} data-testid="practice-site-suggestion-dismiss"
          onClick={() => run(() => practiceApi(`/practices/${practiceId}/site-suggestion/dismiss`, { method: "POST" }))}>Ignora</Button>
      </div>
    </div>}

    {info?.is_site && linked && <div className="site-flag-linked" data-testid="practice-site-linked">
      <span className="practice-badge success" data-testid="practice-site-badge">CANTIERE ATTIVO</span>
      <button type="button" className="site-flag-open" data-testid="practice-site-open" onClick={() => onNavigate("sites", linked.id)}>
        <Building2 size={16} /><strong>{linked.name}</strong><small>{linked.address || "Indirizzo da completare"}</small><ArrowUpRight size={15} />
      </button>
      {!!impact.companies && <small className="muted">{impact.companies} imprese collegate</small>}
    </div>}
    {info?.is_site && !linked && <div className="practice-notice" data-testid="practice-site-missing">Pratica segnata come cantiere ma nessun cantiere collegato: creane uno o associalo.</div>}
    {!info?.is_site && !mode && !suggestion && <p className="muted" data-testid="practice-site-hint">Non tutte le pratiche sono cantieri. Segnala solo se comporta lavori da seguire in cantiere: il cantiere verrà creato e collegato.</p>}

    {mode === "choose" && <div className="site-flag-choose" data-testid="practice-site-choose">
      <button type="button" className="site-flag-option" data-testid="practice-site-choose-create" onClick={openCreate}>
        <Plus size={17} /><strong>Crea nuovo cantiere</strong><small>Dati precompilati dalla pratica, modificabili</small>
      </button>
      <button type="button" className="site-flag-option" data-testid="practice-site-choose-link" onClick={() => { setSiteId(""); setMode("link"); }}>
        <Link2 size={17} /><strong>Collega cantiere esistente</strong><small>{(info?.sites || []).length} cantieri disponibili per questo committente</small>
      </button>
    </div>}

    {mode === "create" && <form className="site-flag-form" data-testid="practice-site-create-form"
      onSubmit={e => { e.preventDefault(); run(async () => { await send({ mode: "create", ...draft }); setMode(""); }); }}>
      <div className="practice-form-grid">
        <label>Nome cantiere<input required minLength={3} data-testid="practice-site-name" value={draft.name || ""} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
        <label>Indirizzo<input data-testid="practice-site-address" value={draft.address || ""} onChange={e => setDraft({ ...draft, address: e.target.value })} /></label>
        <label>Comune<input data-testid="practice-site-municipality" value={draft.municipality || ""} onChange={e => setDraft({ ...draft, municipality: e.target.value })} /></label>
        <label>Committente<input disabled value={info?.prefill.client_name || "Da associare nella pratica"} data-testid="practice-site-client" /></label>
      </div>
      <label className="practice-archive-option"><input type="checkbox" data-testid="practice-site-coordination" checked={!!draft.is_coordination} onChange={e => setDraft({ ...draft, is_coordination: e.target.checked })} />Cantiere in coordinamento CSE / CSP</label>
      <div className="practice-action-bar">
        <Button type="button" variant="outline" className="ghost" disabled={busy} data-testid="practice-site-create-cancel" onClick={() => setMode("")}>Annulla</Button>
        <Button type="submit" className="primary" disabled={busy} data-testid="practice-site-create-submit">{busy ? "Creazione…" : "Crea e collega il cantiere"}</Button>
      </div>
    </form>}

    {mode === "link" && <form className="site-flag-form" data-testid="practice-site-link-form"
      onSubmit={e => { e.preventDefault(); run(async () => { await send({ mode: "link", site_id: siteId }); setMode(""); }); }}>
      <label>Cantiere esistente<select required data-testid="practice-site-link-select" value={siteId} onChange={e => setSiteId(e.target.value)}>
        <option value="">Seleziona un cantiere</option>
        {(info?.sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select></label>
      <div className="practice-action-bar">
        <Button type="button" variant="outline" className="ghost" disabled={busy} data-testid="practice-site-link-cancel" onClick={() => setMode("choose")}>Indietro</Button>
        <Button type="submit" className="primary" disabled={busy} data-testid="practice-site-link-submit">Collega il cantiere</Button>
      </div>
    </form>}

    {removing && <div className="modal-backdrop">
      <div className="create-modal site-flag-remove" data-testid="practice-site-remove-modal">
        <button type="button" className="modal-close" data-testid="practice-site-remove-close" onClick={() => { setRemoving(false); setConfirm(""); setStep("choice"); }}><X /></button>
        <p className="eyebrow">CANTIERE DELLA PRATICA</p>
        {step === "choice" ? <>
          <h2>La pratica non è un cantiere</h2>
          <p className="muted">La pratica <strong>non viene mai eliminata</strong>. Scegli cosa fare del cantiere collegato{linked ? ` "${linked.name}"` : ""}.</p>
          {linked && <div className="bulk-impact" data-testid="practice-site-remove-impact">
            <strong><AlertTriangle size={14} />L'eliminazione definitiva rimuove anche:</strong>
            <ul>
              <li>{impact.companies || 0} imprese del cantiere</li>
              <li>{impact.files || 0} documenti di cantiere</li>
              <li>{impact.logs || 0} verbali e sopralluoghi di coordinamento</li>
            </ul>
            {!!impact.practices && <small className="muted">Attenzione: il cantiere è collegato ad altre {impact.practices} pratiche, quindi non può essere eliminato.</small>}
          </div>}
          {error && <div className="error" role="alert" data-testid="practice-site-remove-error">{error}</div>}
          <div className="practice-action-bar">
            <Button variant="outline" className="ghost" disabled={busy} data-testid="practice-site-keep"
              onClick={() => run(async () => { await api(practiceId, "", { method: "DELETE" }); setRemoving(false); })}>Scollega e conserva il cantiere</Button>
            {linked && !impact.practices && <Button className="danger" disabled={busy} data-testid="practice-site-delete"
              onClick={() => { setConfirm(""); setError(""); setStep("confirm"); }}><Trash2 size={15} />Elimina definitivamente il cantiere</Button>}
          </div>
        </> : <>
          <h2>Confermi l'eliminazione definitiva?</h2>
          <p className="muted">Stai eliminando il cantiere <strong>{linked?.name}</strong>. L'operazione <strong>non è reversibile</strong>: la pratica resta, il cantiere no.</p>
          <div className="bulk-impact danger" data-testid="practice-site-confirm-impact">
            <strong><AlertTriangle size={14} />Verranno eliminati definitivamente:</strong>
            <ul>
              <li>il cantiere <strong>{linked?.name}</strong>{linked?.address ? ` · ${linked.address}` : ""}</li>
              <li>{impact.companies || 0} imprese collegate e i loro documenti di idoneità</li>
              <li>{impact.files || 0} documenti di cantiere</li>
              <li>{impact.logs || 0} verbali e sopralluoghi di coordinamento</li>
            </ul>
          </div>
          <label className="site-flag-confirm">Per procedere digita <strong>ELIMINA</strong>
            <input autoFocus data-testid="practice-site-confirm-input" value={confirm} placeholder="ELIMINA"
              onChange={e => setConfirm(e.target.value)} />
          </label>
          {error && <div className="error" role="alert" data-testid="practice-site-remove-error">{error}</div>}
          <div className="practice-action-bar">
            <Button variant="outline" className="ghost" disabled={busy} data-testid="practice-site-confirm-cancel"
              onClick={() => { setStep("choice"); setConfirm(""); }}>Annulla</Button>
            <Button className="danger" disabled={busy || confirm.trim().toUpperCase() !== "ELIMINA"} data-testid="practice-site-confirm-delete"
              onClick={() => run(async () => { await api(practiceId, "?delete_site=true", { method: "DELETE" }); setRemoving(false); setStep("choice"); setConfirm(""); })}><Trash2 size={15} />Elimina definitivamente</Button>
          </div>
        </>}
      </div>
    </div>}

    {error && !removing && <div className="error" role="alert" data-testid="practice-site-error">{error}</div>}
  </section>;
};
